import React, { useState, useEffect } from 'react';
import { contactService, ContactInfo } from '../../services/contactService';
import LoadingSpinner from '../ui/LoadingSpinner';

export const ContactManager: React.FC = () => {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadContactInfo();
  }, []);
  
  // Clear success message after 3 seconds
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  const loadContactInfo = async () => {
    try {
      setIsLoading(true);
      const data = await contactService.getContactInfo();
      setContactInfo(data);
      setError(null);
    } catch (err) {
      setError('Failed to load contact information');
      console.error('Error loading contact info:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (!contactInfo) return;
    
    // Handle nested properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setContactInfo({
        ...contactInfo,
        [parent]: {
          ...(contactInfo as any)[parent],
          [child]: value
        }
      });
    } else {
      setContactInfo({
        ...contactInfo,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contactInfo) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      await contactService.updateContactInfo(contactInfo);
      setSaveSuccess(true);
    } catch (err) {
      setError('Failed to save contact information');
      console.error('Error saving contact info:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!contactInfo) {
    return (
      <div className="bg-red-50 p-4 rounded-md text-red-700">
        Unable to load contact information.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold mb-6">Contact Information</h2>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}
      
      {saveSuccess && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md">
          Contact information saved successfully!
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4 pb-2 border-b">General Contact</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phone">
                Phone Number
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={contactInfo.phone}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={contactInfo.email}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                required
              />
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4 pb-2 border-b">Office Address</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="address.street">
                Street Address
              </label>
              <input
                type="text"
                id="address.street"
                name="address.street"
                value={contactInfo.address.street}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="address.city">
                  City
                </label>
                <input
                  type="text"
                  id="address.city"
                  name="address.city"
                  value={contactInfo.address.city}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="address.state">
                  State
                </label>
                <input
                  type="text"
                  id="address.state"
                  name="address.state"
                  value={contactInfo.address.state}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="address.zipCode">
                  ZIP Code
                </label>
                <input
                  type="text"
                  id="address.zipCode"
                  name="address.zipCode"
                  value={contactInfo.address.zipCode}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                  required
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-700 mb-4 pb-2 border-b">Business Hours</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="businessHours.monday">
                Monday
              </label>
              <input
                type="text"
                id="businessHours.monday"
                name="businessHours.monday"
                value={contactInfo.businessHours.monday}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                placeholder="e.g. 9AM - 5PM"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="businessHours.tuesday">
                Tuesday
              </label>
              <input
                type="text"
                id="businessHours.tuesday"
                name="businessHours.tuesday"
                value={contactInfo.businessHours.tuesday}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                placeholder="e.g. 9AM - 5PM"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="businessHours.wednesday">
                Wednesday
              </label>
              <input
                type="text"
                id="businessHours.wednesday"
                name="businessHours.wednesday"
                value={contactInfo.businessHours.wednesday}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                placeholder="e.g. 9AM - 5PM"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="businessHours.thursday">
                Thursday
              </label>
              <input
                type="text"
                id="businessHours.thursday"
                name="businessHours.thursday"
                value={contactInfo.businessHours.thursday}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                placeholder="e.g. 9AM - 5PM"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="businessHours.friday">
                Friday
              </label>
              <input
                type="text"
                id="businessHours.friday"
                name="businessHours.friday"
                value={contactInfo.businessHours.friday}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                placeholder="e.g. 9AM - 5PM"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="businessHours.saturday">
                Saturday
              </label>
              <input
                type="text"
                id="businessHours.saturday"
                name="businessHours.saturday"
                value={contactInfo.businessHours.saturday}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                placeholder="e.g. 10AM - 2PM or Closed"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="businessHours.sunday">
                Sunday
              </label>
              <input
                type="text"
                id="businessHours.sunday"
                name="businessHours.sunday"
                value={contactInfo.businessHours.sunday}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-accent-500 focus:ring-accent-500"
                placeholder="e.g. Closed"
                required
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactManager; 